import { BACKEND_URL } from './apiBase';
/**
 * Helpers for the homepage rows that show the most-clicked experiences.
 *
 * Click counts are sourced from /api/experiences/click-stats (anonymous,
 * cached for the page session). Sections rank experiences by total clicks
 * (descending), then either keep that order (Events) or shuffle the
 * top-N for variety on every visit (Top Experiences / Places / Things to do).
 */

/**
 * Fetch the click-stats map once and cache it on `window` so the four
 * sections that need it don't fan out to four parallel requests.
 * Returns: { [experienceId: string]: number }
 */
export async function fetchClickStats() {
  if (typeof window !== 'undefined' && window.__clickStatsPromise) {
    return window.__clickStatsPromise;
  }
  const promise = (async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/experiences/click-stats`);
      const data = await res.json();
      return data.stats || {};
    } catch (err) {
      console.error('Failed to fetch click stats:', err);
      return {};
    }
  })();
  if (typeof window !== 'undefined') window.__clickStatsPromise = promise;
  return promise;
}

/** Fisher–Yates in-place shuffle. Returns the same array for chaining. */
export function shuffleInPlace(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Sort `list` by click count (desc), take the top `count`, optionally
 * shuffle. Stable for ties (incl. all-zero on a brand-new site) so we
 * still return a deterministic top-N before the optional shuffle.
 *
 * When `shuffle` is on, callers can pass a `cacheKey` (e.g. "top-exp",
 * "places", "things") so the shuffled order is remembered on `window` for
 * the lifetime of the page. If the set of ranked IDs hasn't changed (as
 * happens on a language switch — same cards, different text), we return
 * the cached order untouched instead of producing a new random permutation
 * that would visibly reshuffle the homepage row under the user.
 */
export function rankByClicks(list, stats, { count = 7, shuffle = false, cacheKey = null } = {}) {
  const sorted = list
    .map((exp, idx) => ({ exp, idx, clicks: stats[exp.id] || 0 }))
    .sort((a, b) => (b.clicks - a.clicks) || (a.idx - b.idx))
    .slice(0, count)
    .map((row) => row.exp);
  if (!shuffle) return sorted;

  // Cache the shuffled permutation per (key, id-set) so repeated calls
  // with the same input return the same order. Falls through to a fresh
  // shuffle only when the pool of IDs changes (e.g. admin adds/removes a card).
  if (cacheKey && typeof window !== 'undefined') {
    const idsSig = sorted.map((e) => e.id).join(',');
    window.__shuffleCache = window.__shuffleCache || {};
    const prev = window.__shuffleCache[cacheKey];
    if (prev && prev.idsSig === idsSig) {
      const byId = Object.fromEntries(sorted.map((e) => [e.id, e]));
      // Use the cached order; discard any id that disappeared from `sorted`.
      const ordered = prev.order.map((id) => byId[id]).filter(Boolean);
      if (ordered.length === sorted.length) return ordered;
    }
    const shuffled = shuffleInPlace(sorted.slice());
    window.__shuffleCache[cacheKey] = {
      idsSig,
      order: shuffled.map((e) => e.id),
    };
    return shuffled;
  }

  return shuffleInPlace(sorted.slice());
}

/**
 * Same contract as `rankByClicks` but GUARANTEES at least `minFromLocation`
 * cards in the returned set have the specified destination (case-insensitive
 * substring match on `exp.destination`). Used by the homepage rows to keep
 * Sitges visible even when it's not the most-clicked destination.
 *
 * Algorithm:
 *   1. Rank the full pool by clicks desc (same as rankByClicks, no slice yet).
 *   2. Take the natural top-`count`.
 *   3. Count how many of those match `location`. If >= min, we're done.
 *   4. Otherwise, swap the lowest-ranked NON-matches out and the highest-ranked
 *      matches that weren't picked in. Stop when we hit the minimum or run
 *      out of matching candidates (small pool degrades gracefully).
 *   5. Optionally shuffle — with the same session cache as `rankByClicks`.
 */
export function rankByClicksWithMinLocation(
  list,
  stats,
  {
    count = 7,
    shuffle = false,
    cacheKey = null,
    location = 'sitges',
    minFromLocation = 3,
  } = {},
) {
  const matches = (exp) =>
    (exp.destination || '').toLowerCase().includes(location.toLowerCase());

  // Rank EVERY candidate (no slice) so we can promote matches from below the cutoff.
  const rankedAll = list
    .map((exp, idx) => ({ exp, idx, clicks: stats[exp.id] || 0 }))
    .sort((a, b) => (b.clicks - a.clicks) || (a.idx - b.idx))
    .map((row) => row.exp);

  // Split preserving rank order within each bucket.
  const topIds = new Set(rankedAll.slice(0, count).map((e) => e.id));
  const picked = rankedAll.filter((e) => topIds.has(e.id));
  const unpickedMatches = rankedAll.filter((e) => !topIds.has(e.id) && matches(e));

  const needed = Math.max(0, minFromLocation - picked.filter(matches).length);

  if (needed > 0 && unpickedMatches.length > 0) {
    const incoming = unpickedMatches.slice(0, needed);
    // Find indices of the lowest-ranked non-matches and replace them.
    const swapIdxs = [];
    for (let i = picked.length - 1; i >= 0 && swapIdxs.length < incoming.length; i--) {
      if (!matches(picked[i])) swapIdxs.push(i);
    }
    swapIdxs.forEach((idx, n) => {
      if (n < incoming.length) picked[idx] = incoming[n];
    });
    // Re-sort the final set back into rank order so ties stay stable before shuffle.
    picked.sort(
      (a, b) => (stats[b.id] || 0) - (stats[a.id] || 0) || 0,
    );
  }

  if (!shuffle) return picked;

  if (cacheKey && typeof window !== 'undefined') {
    const idsSig = picked.map((e) => e.id).join(',');
    window.__shuffleCache = window.__shuffleCache || {};
    const prev = window.__shuffleCache[cacheKey];
    if (prev && prev.idsSig === idsSig) {
      const byId = Object.fromEntries(picked.map((e) => [e.id, e]));
      const ordered = prev.order.map((id) => byId[id]).filter(Boolean);
      if (ordered.length === picked.length) return ordered;
    }
    const shuffled = shuffleInPlace(picked.slice());
    window.__shuffleCache[cacheKey] = {
      idsSig,
      order: shuffled.map((e) => e.id),
    };
    return shuffled;
  }

  return shuffleInPlace(picked.slice());
}
